import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * Larguras de coluna ajustáveis pelo usuário, no estilo planilha.
 *
 * Por que existe: uma tabela com `table-layout: auto` deixa cada coluna crescer
 * até o conteúdo. Num notebook isso empurra metade das colunas para fora da
 * tela e nenhum `truncate` funciona, porque não há largura máxima que o force.
 * Fixar as larguras resolve o corte — mas larguras fixas escolhidas por nós
 * nunca servem para todo mundo, então quem usa precisa poder arrastar.
 *
 * Genérico de propósito: a tabela de oportunidades é a primeira consumidora,
 * mas nada aqui conhece CRM. Basta uma lista de colunas e uma chave de
 * armazenamento.
 */
export type ResizableColumnDef<K extends string> = {
  key: K;
  /** Largura inicial, em px. */
  width: number;
  /** Piso do arraste, em px. Padrão: 48. */
  minWidth?: number;
};

const FALLBACK_MIN_WIDTH = 48;

export function useResizableColumns<K extends string>(
  defs: ResizableColumnDef<K>[],
  storageKey: string,
) {
  // `defs` costuma ser um literal recriado a cada render; congelar por chave
  // evita que os memos abaixo invalidem sem motivo.
  const signature = defs
    .map((d) => `${d.key}:${d.width}:${d.minWidth ?? FALLBACK_MIN_WIDTH}`)
    .join("|");

  const defsRef = useRef(defs);
  defsRef.current = defs;

  const defaults = useMemo(() => {
    const out = {} as Record<K, number>;
    for (const d of defsRef.current) out[d.key] = d.width;
    return out;
  }, [signature]);

  const mins = useMemo(() => {
    const out = {} as Record<K, number>;
    for (const d of defsRef.current) out[d.key] = d.minWidth ?? FALLBACK_MIN_WIDTH;
    return out;
  }, [signature]);

  const [widths, setWidths] = useState<Record<K, number>>(() =>
    readStored(storageKey, defaults, mins),
  );

  // Se a lista de colunas mudar entre versões, completa o que faltar sem
  // descartar o que a pessoa já ajustou.
  useEffect(() => {
    setWidths((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const key of Object.keys(defaults) as K[]) {
        if (typeof next[key] !== "number") {
          next[key] = defaults[key];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [defaults]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(widths));
    } catch {
      /* modo privado / cota cheia: seguir sem persistir */
    }
  }, [storageKey, widths]);

  /** Coluna em arraste, ou `null`. Nome, não booleano: quem desenha o punho
   *  precisa saber QUAL coluna destacar. */
  const [resizingKey, setResizingKey] = useState<K | null>(null);

  /** Dados do arraste em curso. Ref, não estado: muda a cada pixel. */
  const drag = useRef<{ key: K; startX: number; startWidth: number } | null>(
    null,
  );

  const startResize = useCallback(
    (key: K, e: React.PointerEvent<HTMLElement>) => {
      drag.current = { key, startX: e.clientX, startWidth: widths[key] };
      setResizingKey(key);
      // A captura entrega pointermove/pointerup ao próprio punho mesmo quando o
      // cursor sai dele — sem isso, arrastar rápido "solta" a coluna.
      e.currentTarget.setPointerCapture?.(e.pointerId);
    },
    [widths],
  );

  useEffect(() => {
    if (!resizingKey || typeof document === "undefined") return;

    const onMove = (e: PointerEvent) => {
      const d = drag.current;
      if (!d) return;
      const min = mins[d.key] ?? FALLBACK_MIN_WIDTH;
      const next = Math.max(min, Math.round(d.startWidth + (e.clientX - d.startX)));
      setWidths((prev) => (prev[d.key] === next ? prev : { ...prev, [d.key]: next }));
    };

    const stop = () => {
      drag.current = null;
      setResizingKey(null);
    };

    // O cursor precisa continuar `col-resize` mesmo sobre o resto da página,
    // e a seleção de texto precisa parar — senão o arraste vira "selecionar
    // a tabela inteira". Mesmo tratamento do resize da sidebar.
    const prevCursor = document.body.style.cursor;
    const prevSelect = document.body.style.userSelect;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", stop);
    window.addEventListener("pointercancel", stop);

    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", stop);
      window.removeEventListener("pointercancel", stop);
      // Restaurar também no cleanup: desmontar no meio do arraste deixaria o
      // cursor da página travado em col-resize.
      document.body.style.cursor = prevCursor;
      document.body.style.userSelect = prevSelect;
    };
  }, [resizingKey, mins]);

  const resetWidth = useCallback(
    (key: K) => setWidths((prev) => ({ ...prev, [key]: defaults[key] })),
    [defaults],
  );

  const resetWidths = useCallback(() => setWidths({ ...defaults }), [defaults]);

  return { widths, startResize, resetWidth, resetWidths, resizingKey };
}

function readStored<K extends string>(
  storageKey: string,
  defaults: Record<K, number>,
  mins: Record<K, number>,
): Record<K, number> {
  if (typeof window === "undefined") return { ...defaults };
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return { ...defaults };
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const out = { ...defaults };
    for (const key of Object.keys(defaults) as K[]) {
      const v = parsed[key];
      if (typeof v !== "number" || !Number.isFinite(v)) continue;
      // Clampar na leitura: um valor salvo por uma versão anterior pode estar
      // abaixo do mínimo atual e deixaria a coluna ilegível sem explicação.
      out[key] = Math.max(mins[key] ?? FALLBACK_MIN_WIDTH, Math.round(v));
    }
    return out;
  } catch {
    return { ...defaults };
  }
}
